import { Box, Button, Link, Stack, Typography, useTheme } from '@mui/material';
import TourAvailability from 'src/components/domain/upcoming-tours/tour-availability/TourAvailability';
import { useLocale, formatCurrencyNoDecimalsIfPossible } from '@repo/i18n';
import { currencyAtom } from '@repo/widget-utils/currencyAtom';
import { upcomingSelectedTourAtom } from 'src/state/upcomingSelectedTour.atom';
import { ProductInstance } from '@repo/types';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { useAtom } from 'ximple';

import type { JSX } from 'react';

export default function UpcomingTour(props: {
    tour: ProductInstance;
    fewLeft: number;
    showRemaining?: boolean;
    bigImages: boolean;
}) {
    const { t, locale } = useLocale();
    const [currency] = useAtom(currencyAtom);
    const theme = useTheme();
    const customizations = useCustomizations();
    const { url: linkUrl = '', fromPrice } = props.tour.product ?? {};

    return (
        <Box
            display="flex"
            flex="stretch"
            flexDirection="row"
            alignItems="center"
            width="100%"
            borderRadius={`${customizations.borderRadius}px`}
            boxShadow={customizations.imageShadow ? theme.shadows[3] : undefined}
            mb={theme.spacing(1)}
            sx={{
                backgroundColor: customizations.productCardColor,
                justifyContent: 'space-between',
                [theme.breakpoints.down('sm')]: {
                    minHeight: '80px',
                    padding: '0',
                },
            }}
        >
            <Button
                component={Link}
                href={linkUrl}
                aria-label={`Link to book ${props.tour.product?.title} at ${props.tour.start.format(
                    'LT',
                )} on the ${props.tour.start.format('LLL')}`}
                onClick={async (e) => {
                    e.preventDefault();
                    await upcomingSelectedTourAtom.update(props.tour);
                    window.location.href = linkUrl;
                }}
                display="flex"
                flex="stretch"
                flexDirection="row"
                alignItems="center"
                width="100%"
                height="100%"
                sx={{
                    justifyContent: 'space-between',
                    gap: theme.spacing(2),
                    padding: props.bigImages
                        ? { sm: theme.spacing(1), md: theme.spacing(2) }
                        : undefined,
                }}
            >
                <Box
                    component={'img'}
                    sx={{
                        height: {
                            xs: theme.spacing(10),
                            sm: props.bigImages ? theme.spacing(14) : undefined,
                            md: props.bigImages ? theme.spacing(18) : undefined,
                        },
                        width: 'auto',
                        aspectRatio: '16/9',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        borderRadius: `${theme.shape.borderRadius}px`,
                    }}
                    src={props.tour.product?.coverImage.src}
                    alt={props.tour.product?.coverImage.alt}
                />
                <Stack
                    direction={props.bigImages ? 'column' : { xs: 'column', sm: 'row' }}
                    width="100%"
                    justifyContent="space-between"
                    alignItems="left"
                    gap={1}
                >
                    <Box>
                        <UpcomingTourTypography variant="title">
                            {props.tour.product?.title ?? ''}
                        </UpcomingTourTypography>
                        <Stack direction="row">
                            <UpcomingTourTypography variant="normal">
                                {fromPrice ? t.from : ''}
                            </UpcomingTourTypography>
                            <UpcomingTourTypography variant="emphasis">
                                &nbsp;
                                {fromPrice
                                    ? formatCurrencyNoDecimalsIfPossible(
                                          locale,
                                          currency,
                                          fromPrice,
                                      )
                                    : '-'}
                            </UpcomingTourTypography>
                        </Stack>
                    </Box>
                    <Stack
                        alignItems={{
                            xs: 'flex-start',
                            sm: props.bigImages ? undefined : 'flex-end',
                        }}
                        p={props.bigImages ? undefined : { sm: theme.spacing(0, 1) }}
                    >
                        <TourAvailability
                            fewLeft={props.fewLeft}
                            capacity={
                                props.tour.isClosedForBooking ? 0 : (props.tour?.capacity ?? 0)
                            }
                            showRemaining={props.showRemaining}
                            hasWaitingList={props.tour.hasWaitingList}
                            isClosedForBooking={props.tour.isClosedForBooking}
                        />
                        <Stack
                            mt={{ xs: 0.5, sm: 1 }}
                            gap={0.5}
                            direction="row"
                            alignItems="flex-end"
                        >
                            <UpcomingTourTypography variant="normal" noWrap>
                                {props.tour.start.format('D. MMM YYYY')}
                            </UpcomingTourTypography>
                            <UpcomingTourTypography variant="normal" noWrap>
                                -
                            </UpcomingTourTypography>
                            <UpcomingTourTypography variant="emphasis" noWrap>
                                {props.tour.start.format('HH:mm')}
                            </UpcomingTourTypography>
                        </Stack>
                    </Stack>
                </Stack>
            </Button>
        </Box>
    );
}

function UpcomingTourTypography(props: {
    children: JSX.Element | string | string[];
    variant: 'title' | 'normal' | 'emphasis';
    noWrap?: boolean;
}) {
    const customizations = useCustomizations();

    const isTitle = props.variant === 'title';

    return (
        <Typography
            variant={isTitle ? 'h6' : 'body2'}
            noWrap={isTitle || props.noWrap}
            m={0}
            sx={{
                color: isTitle ? customizations.productCardTextColor : undefined,
                fontWeight:
                    props.variant === 'emphasis' ? customizations.boldFontWeight : undefined,
                ...(isTitle && { overflow: 'hidden', textOverflow: 'ellipsis' }),
            }}
        >
            {props.children}
        </Typography>
    );
}
